import React, { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { TaskAction } from '@confluence/fabric-media-support';

import { DescriptionColumn } from './DescriptionColumn';
import { DateColumn } from './DateColumn';
import { MainColumn } from './MainColumn';
import type { TaskPages, TaskSpaces, TaskColumn } from './tasksDefinitions';
import type {
	TasksQuery_inlineTasks_inlineTasks as Tasks,
	TasksQuery_inlineTasks_inlineTasks_container as TaskContainer,
	TasksQuery_inlineTasks_inlineTasks_container_ConfluencePage as ConfluencePage,
	TasksQuery_inlineTasks_inlineTasks_container_ConfluenceBlogPost as ConfluenceBlogPost,
} from './__types__/TasksQuery';
import { ConfluenceInlineTaskStatus } from './__types__/TasksQuery';

export interface UseFormattedTasksProps {
	cursor: number | null;
	mainColumn: TaskColumn;
	tasks: Tasks[];
	onTaskCheckboxClick: (taskAction: TaskAction) => void;
}

export interface TableTaskRow {
	key: string;
	id: string;
	cells: { key: string; content: ReactNode }[];
}

export interface FormattedTasks {
	spaces: TaskSpaces; // Record of spaces for filter list.
	pages: TaskPages; // Record of pages for filter list.
	rows: TableTaskRow[]; // Rows to be rendered by table component.
}

export const isPage = (value: TaskContainer): value is ConfluencePage => {
	return (
		!Boolean((value as ConfluenceBlogPost).blogPostId) && Boolean((value as ConfluencePage).pageId)
	);
};

export const useFormattedTasks = ({
	cursor,
	mainColumn,
	tasks,
	onTaskCheckboxClick,
}: UseFormattedTasksProps) => {
	const [pages, setPages] = useState<TaskPages>({});
	const [spaces, setSpaces] = useState<TaskSpaces>({});

	const { rows } = useMemo<{ rows: TableTaskRow[] }>(() => {
		return tasks.reduce(
			(accumulatedTasks, task) => {
				const taskId = task?.taskId ?? '';
				const rowId = task.id;

				if (!taskId) {
					return accumulatedTasks;
				}

				if (task?.container) {
					// set pages
					if (isPage(task.container)) {
						const pageId = task.container.pageId as string;
						const page = task.container;
						setPages((prev) => ({
							...prev,
							[pageId]: {
								...prev[pageId],
								...page,
							},
						}));
					}

					if (task.container?.space) {
						const spaceId = task.container.space.spaceId as string;

						setSpaces((prev) => ({
							...prev,
							[spaceId]: {
								...prev[spaceId],
								...(task.container || {}).space,
							},
						}));
					}
				}

				// Create the row
				const newRow: TableTaskRow = {
					key: rowId,
					id: rowId,
					cells: [
						{
							key: 'task-description',
							content: (
								<DescriptionColumn
									rowId={rowId}
									adf={task.body?.adf}
									id={taskId}
									defaultChecked={task.status === ConfluenceInlineTaskStatus.COMPLETE}
									onTaskCheckboxClick={onTaskCheckboxClick}
									contentId={
										(task.container && isPage(task.container) ? task.container.pageId : null) ?? ''
									}
								/>
							),
						},
						{
							key: 'task-center',
							content: <MainColumn taskId={rowId} task={task} mainColumn={mainColumn} />,
						},
						{
							key: 'task-due-date',
							content: <DateColumn taskId={rowId} date={task.dueAt ?? ''} />,
						},
					],
				};
				// set rows
				accumulatedTasks.rows.push(newRow);

				return accumulatedTasks;
			},
			{
				rows: [] as TableTaskRow[],
			},
		);
	}, [mainColumn, tasks, onTaskCheckboxClick]);

	return {
		rows: cursor ? rows.slice(0, cursor) : rows,
		pages,
		spaces,
	};
};
